import { useEffect, useCallback, useRef } from "react";

// Import helpers
import { showUploadToast } from "@/utils/image-central-manager/toastHelpers";

// Import reusable components
import { useUppyDropzoneCore } from "@/hooks/imageManager/useUppyDropzoneCore";
import { Spinner } from "../ui/Spinner";

export default function CustomDropzoneCentralModal({
  onChange,
  value = [],
  setError,
  clearErrors,
  dropzoneRef,
  onOpenChange,
  attachUploader,
}) {
  // Receive newImages uploaded via uppy
  // Memoize the onFilesProcessed callback to prevent infinite re-renders
  const handleFilesProcessed = useCallback(
    (newImages) => {
      // Check if newImages is valid
      if (!Array.isArray(newImages) || newImages.length === 0) {
        showUploadToast({
          type: "error",
          title: "No images received",
          subtitle: "Something went wrong while uploading.",
        });
        return;
      }

      // Check if the value array is valid
      if (!Array.isArray(value)) {
        showUploadToast({
          type: "error",
          title: "Unexpected error",
          subtitle: "Image list is invalid or corrupted.",
        });
        return;
      }

      const formatted = newImages.map((img) => {
        if (!img || typeof img !== "object") return null;

        return {
          instanceId: img.instanceId ?? crypto.randomUUID(),
          id: img.id ?? null,
          url: typeof img.url === "string" ? img.url : "",
          name: typeof img.name === "string" ? img.name : "",
          alt: typeof img.alt === "string" ? img.alt : "",
          file: img.file ?? null,
          tags: Array.isArray(img.tags) ? img.tags : [],
          meta: typeof img.meta === "object" && img.meta !== null ? img.meta : {},
        };
      }).filter(Boolean);

      onChange([...value, ...formatted]);
    },
    [onChange, value]
  );

  const { getRootProps, getInputProps, isDragActive, open, isUploading, uploadingCount } =
    useUppyDropzoneCore({
      dropzoneRef,
      setError,
      clearErrors,
      exposeOpen: true,
      validate: false,
      onFilesProcessed: handleFilesProcessed,
      attachUploader,
      onUploadComplete: ({ successCount, errors }) => {
        showUploadToast({
          type: errors?.length ? "error" : "success",
          title: errors?.length ? "Upload failed" : "Upload complete",
          subtitle: errors?.length && successCount > 0 ? `${successCount} succeeded.` : undefined,
          details: errors,
        });
      },
    });

  useEffect(() => {
    // Expose dropzone `open` method to parent via onOpenChange callback
    if (typeof onOpenChange === "function") {
      onOpenChange(open);
    }
  }, [onOpenChange, open]);

  return (
    <div
      {...getRootProps()}
      className={`relative w-full border-2 border-dashed rounded-lg px-6 py-8 flex flex-col items-center justify-center text-center cursor-pointer transition 
            ${isDragActive ? "bg-[#f0f0f0] border-[#aaa]" : "bg-[#fcfcfc] border-[#c7c7c7]"}`}
    >
      <input {...getInputProps()} />

      <button
        type="button"
        onClick={() => {
          open();
        }}
        className="text-sm font-medium shadow-sm bg-white border border-[#a0a0a0] rounded-md px-[10px] py-[5px] hover:bg-[#f3f3f3] transition"
      >
        {isUploading ? "Uploading..." : "Upload image"}
      </button>

      <p className="mt-2 text-[14px] font-medium text-[#666]">
        or drag and drop PNG, JPEG, JPG images
      </p>

      {isUploading && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] rounded-lg flex items-center justify-center">
          <Spinner size="small">
            <p className="text-sm text-[#444] mt-2">
              Uploading...
            </p>
          </Spinner>
        </div>
      )}
    </div>
  );
}
